import { Controller } from "@hotwired/stimulus";
import Cleave from "cleave.js";
import "cleave.js/dist/addons/cleave-phone.us";

// Connects to data-controller="payment"
export default class extends Controller {
  static targets = [
    "cardNumber", "expDate", "cvc", "cardName",
    "achAccountName", "achRoutingNumber", "achAccountNumber", "achAccountType", "achBankName", "achAuthorizationCheckbox",
    "paymentTabs", "paymentTabContent", "cardTab", "achTab", "cardPane", "achPane", "paymentMethodKind",
    "submitButton", "convenienceFeeLine", "convenienceFeeAmount", "taxAmount", "totalAmount", "dueAmount",
    "achProfileCharge", "clientIpv4"
  ];
  static values = {
    clientKey: String,
    apiLoginId: String,
    acceptjsUrl: String,
  };

  connect() {
    // Ensure overlay is hidden when controller is connected (handles back button case)
    this.hideOverlay();
    this.ensureAcceptJsLoaded();
    this.fetchClientIpv4();
    if (this.hasCardNumberTarget) {
      this.cardNumberCleave = new Cleave(this.cardNumberTarget, {
        creditCard: true,
      });
      this.cardNumberTarget.addEventListener("blur", this.validateCardNumber);
      this.cardNumberTarget.addEventListener("change", this.validateCardNumber);
    }
    if (this.hasExpDateTarget) {
      this.expDateCleave = new Cleave(this.expDateTarget, {
        date: true,
        datePattern: ["m", "y"],
      });
      this.expDateTarget.addEventListener("blur", this.validateExpDate);
      this.expDateTarget.addEventListener("change", this.validateExpDate);
    }
    if (this.hasCvcTarget) {
      this.cvcTarget.addEventListener("blur", this.validateCvc);
      this.cvcTarget.addEventListener("change", this.validateCvc);
    }
    if (this.hasCardNameTarget) {
      this.cardNameTarget.addEventListener("blur", this.validateCardName);
      this.cardNameTarget.addEventListener("change", this.validateCardName);
    }

    // ACH field listeners
    if (this.hasAchRoutingNumberTarget) {
      this.achRoutingNumberCleave = new Cleave(this.achRoutingNumberTarget, {
        numericOnly: true,
        blocks: [9],
      });
      this.achRoutingNumberTarget.addEventListener("blur", this.validateAchRoutingNumber);
      this.achRoutingNumberTarget.addEventListener("change", this.validateAchRoutingNumber);
      this.achRoutingNumberTarget.addEventListener("input", this.validateAchRoutingNumber);
    }
    if (this.hasAchAccountNumberTarget) {
      this.achAccountNumberCleave = new Cleave(this.achAccountNumberTarget, {
        numericOnly: true,
        blocks: [17],
      });
      this.achAccountNumberTarget.addEventListener("blur", this.validateAchAccountNumber);
      this.achAccountNumberTarget.addEventListener("change", this.validateAchAccountNumber);
      this.achAccountNumberTarget.addEventListener("input", this.validateAchAccountNumber);
    }
    if (this.hasAchAccountNameTarget) {
      this.achAccountNameTarget.addEventListener("blur", this.validateAchAccountName);
      this.achAccountNameTarget.addEventListener("change", this.validateAchAccountName);
      this.achAccountNameTarget.addEventListener("input", this.updateAccountNameCounter);
      this.updateAccountNameCounter();
    }
    if (this.hasAchAccountTypeTarget) {
      this.achAccountTypeTarget.addEventListener("blur", this.validateAchAccountType);
      this.achAccountTypeTarget.addEventListener("change", this.validateAchAccountType);
    }
    if (this.hasAchBankNameTarget) {
      this.achBankNameTarget.addEventListener("blur", this.validateAchBankName);
      this.achBankNameTarget.addEventListener("change", this.validateAchBankName);
    }
    if (this.hasAchAuthorizationCheckboxTarget) {
      this.achAuthorizationCheckboxTarget.addEventListener("change", this.validateAchAuthorizationCheckbox);
    }

    const form = this.element.querySelector("form");
    if (form) {
      form.addEventListener("submit", this.handleSubmit);
    }
    this.currentPaymentMethod = "card";
    this.updateFieldRequirements();
  }

  switchPaymentMethod = (event) => {
    const tab = event.currentTarget;
    const isAch = tab.id === "ach-tab";
    this.currentPaymentMethod = isAch ? "ach" : "card";

    if (this.hasPaymentMethodKindTarget) {
      this.paymentMethodKindTarget.value = this.currentPaymentMethod;
    }

    // Update button text with appropriate amount
    if (this.hasSubmitButtonTarget) {
      if (isAch) {
        // Differentiate button text based on saved profile
        if (this.hasAchProfileChargeTarget) {
          // Use i18n text from data attribute
          this.submitButtonTarget.textContent = this.submitButtonTarget.dataset.achProfileButton;
        } else {
          this.submitButtonTarget.textContent = 'Authorize and Pay from Bank Account';
        }
      } else {
        const amountFormatted = this.submitButtonTarget.dataset.cardAmountFormatted;
        this.submitButtonTarget.textContent = `Pay ${amountFormatted}`;
      }
    }

    // Update order summary (handle multiple instances for mobile/desktop)
    if (isAch) {
      // Hide convenience fee line for ACH
      if (this.hasConvenienceFeeLineTarget) {
        this.convenienceFeeLineTargets.forEach(el => {
          el.style.setProperty('display', 'none', 'important');
        });
      }
      // Update tax amount for ACH (without convenience fee)
      if (this.hasTaxAmountTarget) {
        this.taxAmountTargets.forEach(el => {
          el.textContent = el.dataset.achTax;
        });
      }
      // Update total amount
      if (this.hasTotalAmountTarget) {
        this.totalAmountTargets.forEach(el => {
          el.textContent = el.dataset.achTotal;
        });
      }
      // Update due amount
      if (this.hasDueAmountTarget) {
        this.dueAmountTargets.forEach(el => {
          el.textContent = el.dataset.achDue;
        });
      }
    } else {
      // Show convenience fee line for credit card
      if (this.hasConvenienceFeeLineTarget) {
        this.convenienceFeeLineTargets.forEach(el => {
          el.style.setProperty('display', 'flex', 'important');
        });
      }
      // Update tax amount for credit card (with convenience fee)
      if (this.hasTaxAmountTarget) {
        this.taxAmountTargets.forEach(el => {
          el.textContent = el.dataset.cardTax;
        });
      }
      // Update total amount
      if (this.hasTotalAmountTarget) {
        this.totalAmountTargets.forEach(el => {
          el.textContent = el.dataset.cardTotal;
        });
      }
      // Update due amount
      if (this.hasDueAmountTarget) {
        this.dueAmountTargets.forEach(el => {
          el.textContent = el.dataset.cardDue;
        });
      }
    }

    this.updateFieldRequirements();
    this.clearValidationErrors();
  }

  updateFieldRequirements() {
    const isAch = this.currentPaymentMethod === "ach";
    const hasProfileCharge = this.hasAchProfileChargeTarget;

    if (this.hasCardNumberTarget) {
      this.cardNumberTarget.required = !isAch;
    }
    if (this.hasExpDateTarget) {
      this.expDateTarget.required = !isAch;
    }
    if (this.hasCvcTarget) {
      this.cvcTarget.required = !isAch;
    }
    if (this.hasCardNameTarget) {
      this.cardNameTarget.required = !isAch;
    }

    // ACH fields only required if not using saved profile
    if (this.hasAchAccountNameTarget) {
      this.achAccountNameTarget.required = isAch && !hasProfileCharge;
    }
    if (this.hasAchRoutingNumberTarget) {
      this.achRoutingNumberTarget.required = isAch && !hasProfileCharge;
    }
    if (this.hasAchAccountNumberTarget) {
      this.achAccountNumberTarget.required = isAch && !hasProfileCharge;
    }
    if (this.hasAchAccountTypeTarget) {
      this.achAccountTypeTarget.required = isAch && !hasProfileCharge;
    }
    if (this.hasAchBankNameTarget) {
      this.achBankNameTarget.required = isAch && !hasProfileCharge;
    }
    if (this.hasAchAuthorizationCheckboxTarget) {
      this.achAuthorizationCheckboxTarget.required = isAch && !hasProfileCharge;
    }
  }

  clearValidationErrors() {
    const isAch = this.currentPaymentMethod === "ach";

    if (isAch) {
      if (this.hasCardNumberTarget) {
        this.cardNumberTarget.classList.remove("is-invalid");
        const feedback = document.getElementById("card-number-feedback");
        if (feedback) feedback.textContent = "";
      }
      if (this.hasExpDateTarget) {
        this.expDateTarget.classList.remove("is-invalid");
        const feedback = document.getElementById("exp-date-feedback");
        if (feedback) feedback.textContent = "";
      }
      if (this.hasCvcTarget) {
        this.cvcTarget.classList.remove("is-invalid");
        const feedback = document.getElementById("cvc-feedback");
        if (feedback) feedback.textContent = "";
      }
      if (this.hasCardNameTarget) {
        this.cardNameTarget.classList.remove("is-invalid");
        const feedback = document.getElementById("card-name-feedback");
        if (feedback) feedback.textContent = "";
      }
    } else {
      if (this.hasAchAccountNameTarget) {
        this.achAccountNameTarget.classList.remove("is-invalid");
        const feedback = document.getElementById("ach-account-name-feedback");
        if (feedback) feedback.textContent = "";
      }
      if (this.hasAchRoutingNumberTarget) {
        this.achRoutingNumberTarget.classList.remove("is-invalid");
        const feedback = document.getElementById("ach-routing-number-feedback");
        if (feedback) feedback.textContent = "";
      }
      if (this.hasAchAccountNumberTarget) {
        this.achAccountNumberTarget.classList.remove("is-invalid");
        const feedback = document.getElementById("ach-account-number-feedback");
        if (feedback) feedback.textContent = "";
      }
      if (this.hasAchAccountTypeTarget) {
        this.achAccountTypeTarget.classList.remove("is-invalid");
        const feedback = document.getElementById("ach-account-type-feedback");
        if (feedback) feedback.textContent = "";
      }
      if (this.hasAchBankNameTarget) {
        this.achBankNameTarget.classList.remove("is-invalid");
        const feedback = document.getElementById("ach-bank-name-feedback");
        if (feedback) feedback.textContent = "";
      }
      if (this.hasAchAuthorizationCheckboxTarget) {
        this.achAuthorizationCheckboxTarget.classList.remove("is-invalid");
        const feedback = document.getElementById("ach-authorization-agreement-feedback");
        if (feedback) feedback.textContent = "";
      }
    }
  }

  disconnect() {
    if (this.cardNumberCleave) {
      this.cardNumberCleave.destroy();
      this.cardNumberTarget.removeEventListener(
        "blur",
        this.validateCardNumber,
      );
      this.cardNumberTarget.removeEventListener(
        "change",
        this.validateCardNumber,
      );
    }
    if (this.expDateCleave) {
      this.expDateCleave.destroy();
      this.expDateTarget.removeEventListener("blur", this.validateExpDate);
      this.expDateTarget.removeEventListener("change", this.validateExpDate);
    }
    if (this.hasCvcTarget) {
      this.cvcTarget.removeEventListener("blur", this.validateCvc);
      this.cvcTarget.removeEventListener("change", this.validateCvc);
    }
    if (this.hasCardNameTarget) {
      this.cardNameTarget.removeEventListener("blur", this.validateCardName);
      this.cardNameTarget.removeEventListener("change", this.validateCardName);
    }
    if (this.achRoutingNumberCleave) {
      this.achRoutingNumberCleave.destroy();
      this.achRoutingNumberTarget.removeEventListener("blur", this.validateAchRoutingNumber);
      this.achRoutingNumberTarget.removeEventListener("change", this.validateAchRoutingNumber);
      this.achRoutingNumberTarget.removeEventListener("input", this.validateAchRoutingNumber);
    }
    if (this.achAccountNumberCleave) {
      this.achAccountNumberCleave.destroy();
      this.achAccountNumberTarget.removeEventListener("blur", this.validateAchAccountNumber);
      this.achAccountNumberTarget.removeEventListener("change", this.validateAchAccountNumber);
      this.achAccountNumberTarget.removeEventListener("input", this.validateAchAccountNumber);
    }
    if (this.hasAchAccountNameTarget) {
      this.achAccountNameTarget.removeEventListener("blur", this.validateAchAccountName);
      this.achAccountNameTarget.removeEventListener("change", this.validateAchAccountName);
      this.achAccountNameTarget.removeEventListener("input", this.updateAccountNameCounter);
    }
    if (this.hasAchAuthorizationCheckboxTarget) {
      this.achAuthorizationCheckboxTarget.removeEventListener("change", this.validateAchAuthorizationCheckbox);
    }
    const form = this.element.querySelector("form");
    if (form) {
      form.removeEventListener("submit", this.handleSubmit);
    }
  }

  showOverlay() {
    const overlay = document.getElementById("payment-overlay");
    if (overlay) {
      overlay.classList.remove("d-none");
      document.body.classList.add("no-freeze-spinner");
    }
  }

  hideOverlay() {
    const overlay = document.getElementById("payment-overlay");
    if (overlay) {
      overlay.classList.add("d-none");
      document.body.classList.remove("no-freeze-spinner");
    }
  }

  showError(message) {
    let errorDiv = document.getElementById("payment-errors");
    if (!errorDiv) {
      errorDiv = document.createElement("div");
      errorDiv.id = "payment-errors";
      errorDiv.className = "alert alert-danger mt-3";
      errorDiv.setAttribute("role", "alert");
      const form = this.element.querySelector("form");
      if (form) {
        form.insertBefore(errorDiv, form.firstChild);
      } else {
        this.element.appendChild(errorDiv);
      }
    }
    errorDiv.textContent = message;
    errorDiv.classList.remove("d-none");
    errorDiv.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  handleSubmit = (e) => {
    e.preventDefault(); // Always prevent default form submission

    let acceptTimeout = null;

    try {
      this.showOverlay();

      // 1. Validate billing address
      const validBilling = this.validateBillingAddress();

      // 2. Validate payment method specific fields
      let paymentValid = false;
      if (this.currentPaymentMethod === "ach") {
        // Skip validation if using saved ACH profile
        if (this.hasAchProfileChargeTarget) {
          paymentValid = true;
        } else {
          const validAchAccountName = this.validateAchAccountName();
          const validAchRoutingNumber = this.validateAchRoutingNumber();
          const validAchAccountNumber = this.validateAchAccountNumber();
          const validAchAccountType = this.validateAchAccountType();
          const validAchBankName = this.validateAchBankName();
          const validAchAuthorization = this.validateAchAuthorizationCheckbox();
          paymentValid = validAchAccountName && validAchRoutingNumber &&
                         validAchAccountNumber && validAchAccountType && validAchBankName &&
                         validAchAuthorization;
        }
      } else {
        const validCardNumber = this.validateCardNumber();
        const validExpDate = this.validateExpDate();
        const validCvc = this.validateCvc();
        const validCardName = this.validateCardName();
        paymentValid = validCardNumber && validExpDate && validCvc && validCardName;
      }

      if (!(paymentValid && validBilling)) {
        // Focus first invalid field
        const firstInvalid = this.element.querySelector(".is-invalid");
        if (firstInvalid) firstInvalid.focus();
        this.hideOverlay();
        return;
      }

      // 3. Handle ACH vs Credit Card submission
      if (this.currentPaymentMethod === "ach") {
        // For ACH, submit directly (no tokenization needed)
        const form = this.element.querySelector("form");
        if (form) form.submit();
        return;
      }

      // 4. Credit Card: Prepare data for Accept.js
      const cardNumber = this.cardNumberTarget.value.replace(/\s+/g, "");
      const exp = this.expDateTarget.value.split("/");
      const expMonth = exp[0].trim();
      let expYear = exp[1].trim();
      if (expYear.length === 2) expYear = "20" + expYear;
      const cardCode = this.cvcTarget.value.trim();

      // 5. Accept.js credentials
      const authData = {
        clientKey: this.clientKeyValue,
        apiLoginID: this.apiLoginIdValue,
      };

      const cardData = {
        cardNumber: cardNumber,
        month: expMonth,
        year: expYear,
        cardCode: cardCode,
      };

      const secureData = {
        authData: authData,
        cardData: cardData,
      };

      acceptTimeout = setTimeout(() => {
        this.hideOverlay();
        this.showError("Payment processing timed out. Please try again.");
        console.error("[payment] Accept.js timeout - callback not called within 30 seconds", {
          controller: "payment",
          action: "handleSubmit",
        });
      }, 30000);

      Accept.dispatchData(secureData, (response) => {
          clearTimeout(acceptTimeout);

          try {
            if (response.messages.resultCode === "Ok") {
              // Fill hidden fields with opaqueData
              const opaqueData = response.opaqueData;
              document.getElementById("opaqueDataDescriptor").value =
                opaqueData.dataDescriptor;
              document.getElementById("opaqueDataValue").value = opaqueData.dataValue;
              // Submit the form
              const form = this.element.querySelector("form");
              if (form) form.submit();
            } else {
              this.hideOverlay();
              // Show Accept.js error
              const errorMsg = response.messages.message[0].text;

              const error = new Error(`Accept.js payment error: ${errorMsg}`);
              console.error("[payment] Accept.js error:", error, {
                controller: "payment",
                resultCode: response.messages.resultCode,
                messageCode: response.messages.message[0].code,
              });

              this.showError(errorMsg);
            }
          } catch (error) {
            this.hideOverlay();
            console.error("[payment]", error, {
              controller: "payment",
              action: "acceptjs_callback",
            });

            this.showError("An error occurred processing your payment. Please try again.");
          }
        });
    } catch (error) {
      if (acceptTimeout) {
        clearTimeout(acceptTimeout);
      }
      this.hideOverlay();
      console.error("[payment]", error, {
        controller: "payment",
        action: "handleSubmit",
      });

      this.showError("An error occurred. Please try again.");
    }
  };

  validateBillingAddress = () => {
    // If billing fields are present on the page, validate them
    const street = document.getElementById("billing-street");
    const city = document.getElementById("billing-city");
    const state = document.getElementById("billing-state");
    const zip = document.getElementById("billing-zip");

    const fields = [street, city, state, zip].filter(Boolean);
    if (fields.length === 0) return true; // No billing fields present

    let allValid = true;
    fields.forEach((el) => {
      const value = (el.value || "").trim();
      const valid = value.length > 0;
      this.setFieldValidity(el, `${el.id}-feedback`, valid, "This field is required.");
      if (!valid) allValid = false;
    });

    return allValid;
  };

  validateCardNumber = () => {
    if (!this.hasCardNumberTarget) return true;
    const value = this.cardNumberTarget.value.replace(/\s+/g, "");
    let valid = value.length >= 13 && value.length <= 19 && /^\d+$/.test(value);
    this.setFieldValidity(
      this.cardNumberTarget,
      "card-number-feedback",
      valid,
      valid ? "" : "Card number is invalid.",
    );
    return valid;
  };

  validateExpDate = () => {
    if (!this.hasExpDateTarget) return true;
    const value = this.expDateTarget.value.trim();
    const match = value.match(/^(\d{2})\/(\d{2,4})$/);
    let valid = false;
    let msg = "";
    if (!match) {
      msg = "Expiration date format is invalid.";
    } else {
      let [_, mm, yy] = match;
      mm = parseInt(mm, 10);
      yy = parseInt(yy, 10);
      if (yy < 100) {
        yy += 2000;
      }
      const now = new Date();
      const thisMonth = now.getMonth() + 1;
      const thisYear = now.getFullYear();
      if (
        mm < 1 ||
        mm > 12 ||
        yy < thisYear ||
        (yy === thisYear && mm < thisMonth)
      ) {
        msg = "Expiration date is in the past.";
      } else {
        valid = true;
      }
    }
    this.setFieldValidity(this.expDateTarget, "exp-date-feedback", valid, msg);
    return valid;
  };

  validateCvc = () => {
    if (!this.hasCvcTarget) return true;
    const value = this.cvcTarget.value.trim();
    let valid = /^\d{3,4}$/.test(value);
    this.setFieldValidity(
      this.cvcTarget,
      "cvc-feedback",
      valid,
      valid ? "" : "CVV is invalid.",
    );
    return valid;
  };

  validateCardName = () => {
    if (!this.hasCardNameTarget) return true;
    const value = this.cardNameTarget.value.trim();
    let valid = value.length > 0;
    this.setFieldValidity(
      this.cardNameTarget,
      "card-name-feedback",
      valid,
      valid ? "" : "Name on Card is required.",
    );
    return valid;
  };

  setFieldValidity(input, feedbackId, valid, msg) {
    let feedback = document.getElementById(feedbackId);
    if (!feedback) {
      // Dynamically create the feedback div if it doesn't exist
      feedback = document.createElement("div");
      feedback.id = feedbackId;
      feedback.className = "invalid-feedback";
      // Insert after the input
      if (input.nextSibling) {
        input.parentNode.insertBefore(feedback, input.nextSibling);
      } else {
        input.parentNode.appendChild(feedback);
      }
    }
    if (input && feedback) {
      if (!valid) {
        input.classList.add("is-invalid");
        feedback.textContent = msg;
      } else {
        input.classList.remove("is-invalid");
        feedback.textContent = "";
      }
    }
  }

  ensureAcceptJsLoaded() {
    if (
      typeof window.Accept === "undefined" &&
      !document.getElementById("acceptjs-script")
    ) {
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.src = this.acceptjsUrlValue;
      script.id = "acceptjs-script";

      script.onerror = () => {
        const error = new Error("Failed to load Accept.js payment script");
        console.error("[payment]", error, {
          controller: "payment",
          action: "ensureAcceptJsLoaded",
          scriptUrl: this.acceptjsUrlValue,
        });
      };

      document.head.appendChild(script);
    }
  }

  fetchClientIpv4() {
    // Fetch IPv4 address for ACH authorization audit trail
    // Runs async in background, doesn't block form interaction
    if (this.hasClientIpv4Target) {
      fetch("https://api.ipify.org?format=json")
        .then((response) => response.json())
        .then((data) => {
          // Validate IPv4 format and octet ranges (0-255)
          if (data.ip && this.isValidIpv4(data.ip)) {
            this.clientIpv4Target.value = data.ip;
          }
        })
        .catch((error) => {
          // Silently fail - server-side IP will be used as fallback
          console.warn("[payment] Failed to fetch client IPv4:", error);
        });
    }
  }

  isValidIpv4(ip) {
    // Match IPv4 pattern and validate each octet is 0-255
    const octets = ip.match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)$/);
    if (!octets) return false;

    // Check each octet (skip index 0 which is the full match)
    for (let i = 1; i <= 4; i++) {
      const num = parseInt(octets[i], 10);
      if (num < 0 || num > 255) return false;
    }

    return true;
  }

  validateAchAccountName = () => {
    if (!this.hasAchAccountNameTarget) return true;
    const value = this.achAccountNameTarget.value.trim();
    let valid = value.length > 0;
    let msg = "";
    if (!valid) {
      msg = "Name on Account is required.";
    } else if (value.length > 22) {
      valid = false;
      msg = "Account holder name is too long (max 22 characters).";
    }
    this.setFieldValidity(
      this.achAccountNameTarget,
      "ach-account-name-feedback",
      valid,
      msg,
    );
    this.updateAccountNameCounter();
    return valid;
  };

  updateAccountNameCounter = () => {
    if (!this.hasAchAccountNameTarget) return;
    const counter = document.getElementById("ach-account-name-counter");
    if (counter) {
      const length = this.achAccountNameTarget.value.length;
      const maxLength = 22;
      counter.textContent = `${length}/${maxLength}`;
      if (length > maxLength) {
        counter.classList.add("text-danger");
        counter.classList.remove("text-muted");
      } else {
        counter.classList.remove("text-danger");
        counter.classList.add("text-muted");
      }
    }
  };

  validateAchRoutingNumber = () => {
    if (!this.hasAchRoutingNumberTarget) return true;
    const value = this.achRoutingNumberTarget.value.replace(/\D/g, "");
    const valid = value.length === 9 && /^\d{9}$/.test(value);
    this.setFieldValidity(
      this.achRoutingNumberTarget,
      "ach-routing-number-feedback",
      valid,
      valid ? "" : "Routing number must be 9 digits.",
    );
    return valid;
  };

  validateAchAccountNumber = () => {
    if (!this.hasAchAccountNumberTarget) return true;
    const value = this.achAccountNumberTarget.value.trim();
    const valid = value.length >= 5 && value.length <= 17 && /^\d+$/.test(value);
    this.setFieldValidity(
      this.achAccountNumberTarget,
      "ach-account-number-feedback",
      valid,
      valid ? "" : "Account number must be between 5 and 17 digits.",
    );
    return valid;
  };

  validateAchAccountType = () => {
    if (!this.hasAchAccountTypeTarget) return true;
    const value = this.achAccountTypeTarget.value;
    const valid = value === "checking" || value === "savings" || value === "businessChecking";
    this.setFieldValidity(
      this.achAccountTypeTarget,
      "ach-account-type-feedback",
      valid,
      valid ? "" : "Please select an account type.",
    );
    return valid;
  };

  validateAchBankName = () => {
    if (!this.hasAchBankNameTarget) return true;
    const value = this.achBankNameTarget.value.trim();
    let valid = value.length > 0;
    let msg = "";
    if (!valid) {
      msg = "Bank name is required.";
    } else if (value.length > 50) {
      valid = false;
      msg = "Bank name is too long (maximum 50 characters).";
    }
    this.setFieldValidity(
      this.achBankNameTarget,
      "ach-bank-name-feedback",
      valid,
      msg,
    );
    return valid;
  };

  validateAchAuthorizationCheckbox = () => {
    if (!this.hasAchAuthorizationCheckboxTarget) {
      return true;
    }
    const valid = this.achAuthorizationCheckboxTarget.checked;
    this.setFieldValidity(
      this.achAuthorizationCheckboxTarget,
      "ach-authorization-agreement-feedback",
      valid,
      valid ? "" : "You must agree to the ACH Payment Authorization to continue.",
    );
    return valid;
  };
}

